import React, { useState, useEffect } from "react";
import { Spinner, Text, Box } from "@chakra-ui/react";
import MetricCard from "./MetricCard";
import { metricsService, CallQualitySummary } from "../../services/metricsService";

interface CallQualityCardProps {
  onMetricSelect?: (metricName: string) => void;
  businessIdentifier?: string;
  timeRange?: string;
  value?: number;
  trendValue?: number;
}

const CallQualityCard: React.FC<CallQualityCardProps> = ({ 
  onMetricSelect, 
  businessIdentifier,
  timeRange = '7d',
  value: propValue,
  trendValue: propTrendValue 
}) => {
  const [qualityData, setQualityData] = useState<CallQualitySummary | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchQualityData = async () => {
      // If prop values are provided, don't fetch from API (for backward compatibility)
      if (propValue !== undefined && propTrendValue !== undefined) {
        return;
      }

      setIsLoading(true);
      setError(null);
      try {
        const data = await metricsService.getCallQualitySummary(businessIdentifier, timeRange);
        setQualityData(data);
      } catch (err: any) {
        setError(err.message || 'Failed to load call quality data.');
        console.error('Error fetching call quality data:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchQualityData();
  }, [businessIdentifier, timeRange, propValue, propTrendValue]);

  const handleCompare = () => {
    if (onMetricSelect) {
      onMetricSelect("Call Quality");
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minH="200px">
        <Spinner size="lg" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minH="200px">
        <Text color="red.500" textAlign="center">{error}</Text>
      </Box>
    );
  }

  // Use prop values if provided, otherwise use API data, otherwise use fallback
  const displayValue = propValue !== undefined ? propValue : 
    (qualityData?.average_quality_score || 55);
  const trendDisplayValue = propTrendValue !== undefined ? propTrendValue :
    (qualityData?.trend !== undefined ? qualityData.trend : 4);

  return (
    <MetricCard
      title="Call Quality"
      value={displayValue}
      trendValue={trendDisplayValue}
      format="score"
      maxValue={100}
      isPoints={true}
      onCompare={handleCompare}
    />
  );
};

export default CallQualityCard; 